import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import {
  changeRadiusAttrs,
  getRadiusAttrHistory,
  getRadiusAttrs,
  RADIUS_OPS,
  radiusAttrErrorText,
  radiusChangeLine,
  radiusChangeProblem,
  radiusRecordLine,
  type RadiusAttr,
  type RadiusAttrChange,
  type RadiusAttrChangeIn,
  type RadiusAttrChangeRecord,
  type RadiusLoginAttrs,
  type RadiusTable,
} from "../../api/radiusAttributes";
import { sendAccountCode } from "../../api/password";
import { testCodeAccessToken } from "../../api/login";
import { testRadiusLogin } from "../../api/radiusTest";
import type { InvoicePrefSubscriber } from "../../api/invoicePrefs";
import { PasswordField } from "../../components/PasswordField";
import { useResendTimer } from "../../components/useResendTimer";
import { RadiusTestView } from "../tools/SubscriberRadiusTestDialog";

/**
 * RADIUS attributes (special) for one subscriber (owner 2026-10-29: "To owner
 * or superadmin, allow to add or change radius attribute as a special case...
 * check the reply after changing... record this change... Send sms code").
 * The plan's attributes are shown read-only; only the login's own rows change.
 */
export function SubscriberRadiusAttrsDialog({ ns, subscriber, onClose }: { ns: string; subscriber: InvoicePrefSubscriber | null; onClose: () => void }) {
  const identity = useIdentity();
  const accountId = subscriber?.accountid ?? "";
  const attrs = useQuery({
    queryKey: ["radius-attributes", ns, accountId],
    enabled: Boolean(subscriber && identity.token),
    queryFn: ({ signal }) => getRadiusAttrs(identity, ns, accountId, signal),
    retry: false,
  });
  const [showHistory, setShowHistory] = useState(false);
  const history = useQuery({
    queryKey: ["radius-attributes-history", ns, accountId],
    enabled: Boolean(subscriber && identity.token && showHistory),
    queryFn: ({ signal }) => getRadiusAttrHistory(identity, ns, accountId, signal),
    retry: false,
  });
  const [username, setUsername] = useState("");
  const [pending, setPending] = useState<RadiusAttrChangeIn[]>([]);
  const [reason, setReason] = useState("");
  const [reqId, setReqId] = useState("");
  const [code, setCode] = useState("");
  const [done, setDone] = useState<RadiusAttrChange[] | null>(null);
  const resend = useResendTimer();

  useEffect(() => {
    setPending([]);
    setReason("");
    setReqId("");
    setCode("");
    setDone(null);
    setShowHistory(false);
  }, [subscriber]);
  useEffect(() => {
    const logins = attrs.data ?? [];
    if (logins.length && !logins.some((l) => l.username === username)) setUsername(logins[0]!.username);
  }, [attrs.data, username]);

  const sendCode = useMutation({
    mutationFn: () => sendAccountCode(identity, ns, ["sms"], "radius_attributes"),
    onSuccess: (c) => {
      setReqId(c.requestId);
      setCode("");
      resend.start(c.resendIn);
    },
  });
  const save = useMutation({
    mutationFn: () => changeRadiusAttrs(identity, ns, accountId, { username, changes: pending, reason: reason.trim(), accessToken: testCodeAccessToken(reqId, code) }),
    onSuccess: (r) => {
      setDone(r.changes);
      setPending([]);
      setReason("");
      setReqId("");
      setCode("");
      void attrs.refetch();
      if (showHistory) void history.refetch();
    },
    onSettled: () => setCode(""),
  });

  if (!subscriber) return null;
  const login = attrs.data?.find((l) => l.username === username);
  const rowOf = (c: RadiusAttrChangeIn) => (c.id ? [...(login?.check ?? []), ...(login?.reply ?? [])].find((a) => a.id === c.id) : undefined);
  const queue = (c: RadiusAttrChangeIn) => {
    setDone(null);
    setPending((p) => [...p.filter((x) => !(x.id && x.id === c.id && x.table === c.table)), c]);
  };
  const reasonOk = reason.trim().length >= 5;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>RADIUS attributes (special) · {subscriber.name || subscriber.mobile || subscriber.username}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          These change this subscriber's speed, expiry and devices directly, outside the plan. Owner and superadmin only. Each save needs a code sent to your
          mobile, is recorded with your reason, and is emailed to you.
        </Alert>
        {attrs.isLoading && <Typography variant="body2">Loading…</Typography>}
        {attrs.error && <Alert severity="error">{radiusAttrErrorText(attrs.error)}</Alert>}
        {attrs.data && attrs.data.length === 0 && <Alert severity="info">This subscriber has no RADIUS login yet.</Alert>}
        {attrs.data && attrs.data.length > 1 && (
          <TextField select size="small" label="Login" value={username} onChange={(e) => setUsername(e.target.value)} sx={{ mb: 2, minWidth: 240 }} disabled={pending.length > 0}>
            {attrs.data.map((l) => (
              <MenuItem key={l.username} value={l.username}>
                {l.username}
              </MenuItem>
            ))}
          </TextField>
        )}
        {login && <RadiusLoginView login={login} onChange={queue} />}
        {login && <AddAttrForm onAdd={queue} />}

        {pending.length > 0 && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }} data-testid="radius-pending">
            <Typography variant="subtitle2">To save for {username}</Typography>
            {pending.map((c, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {radiusChangeLine(c, rowOf(c))}
                </Typography>
                <Button size="small" onClick={() => setPending((p) => p.filter((_, j) => j !== i))}>
                  Remove
                </Button>
              </Box>
            ))}
            <TextField
              size="small"
              label="Reason (required, recorded)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              inputProps={{ maxLength: 300 }}
              helperText={reasonOk || !reason ? " " : "At least 5 characters"}
            />
            {!reqId ? (
              <Box>
                <Button variant="outlined" disabled={!reasonOk || sendCode.isPending} onClick={() => sendCode.mutate()}>
                  {sendCode.isPending ? "Sending…" : "Send code by SMS"}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  label="Code from SMS"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                />
                <Button size="small" disabled={resend.left > 0 || sendCode.isPending} onClick={() => sendCode.mutate()}>
                  {resend.left > 0 ? `Resend in ${resend.left}s` : "Resend code"}
                </Button>
              </Box>
            )}
            {sendCode.error && <Alert severity="error">{radiusAttrErrorText(sendCode.error)}</Alert>}
            {save.error && <Alert severity="error">{radiusAttrErrorText(save.error)}</Alert>}
          </Box>
        )}

        {done && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success">
              Saved and recorded; the notice email went to you.
              {done.map((c, i) => (
                <Typography key={i} variant="body2" sx={{ fontFamily: "monospace" }}>
                  {radiusChangeLine({ table: c.table, action: c.action, id: c.rowId, attribute: c.attribute, op: c.newOp, value: c.newValue, oldOp: c.oldOp, oldValue: c.oldValue })}
                </Typography>
              ))}
            </Alert>
            <CheckReply ns={ns} accountId={accountId} username={username} />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <Button size="small" onClick={() => setShowHistory((s) => !s)}>
            {showHistory ? "Hide change record" : "Show change record"}
          </Button>
          {showHistory && history.error && <Alert severity="error">{radiusAttrErrorText(history.error)}</Alert>}
          {showHistory && history.data && <RadiusHistoryView changes={history.data} />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {pending.length > 0 && (
          <Button variant="contained" disabled={!reqId || code.length !== 6 || !reasonOk || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : `Save ${pending.length} change${pending.length === 1 ? "" : "s"}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** A login's own rows (editable) and the plan's (read-only). No hooks, so a test can render it. */
export function RadiusLoginView({ login, onChange }: { login: RadiusLoginAttrs; onChange: (c: RadiusAttrChangeIn) => void }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <OwnRows title="This login's own check items" table="check" rows={login.check} onChange={onChange} />
      <OwnRows title="This login's own reply items" table="reply" rows={login.reply} onChange={onChange} />
      <Box>
        <Typography variant="subtitle2">From the plan{login.plan ? ` ${login.plan}` : ""} (read-only; change the plan instead)</Typography>
        {[...login.planCheck.map((a) => ({ ...a, k: "check" })), ...login.planReply.map((a) => ({ ...a, k: "reply" }))].length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }} color="text.secondary">
            {[...login.planCheck, ...login.planReply].map((a) => `${a.attribute} ${a.op} ${a.locked ? "(hidden)" : a.value}`).join(" · ")}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function OwnRows({ title, table, rows, onChange }: { title: string; table: RadiusTable; rows: RadiusAttr[]; onChange: (c: RadiusAttrChangeIn) => void }) {
  const [editing, setEditing] = useState<RadiusAttr | null>(null);
  return (
    <Box>
      <Typography variant="subtitle2">{title}</Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Attribute</TableCell>
                <TableCell>Op</TableCell>
                <TableCell>Value</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id ?? a.attribute}>
                  <TableCell>
                    <code>{a.attribute}</code>
                  </TableCell>
                  <TableCell>
                    <code>{a.op}</code>
                  </TableCell>
                  <TableCell>{a.locked ? <Chip size="small" label="password (Reset password)" /> : <code>{a.value}</code>}</TableCell>
                  <TableCell align="right">
                    {!a.locked && a.id !== undefined && (
                      <>
                        <Button size="small" onClick={() => setEditing(a)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" onClick={() => onChange({ table, action: "delete", id: a.id })}>
                          Delete
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {editing && (
        <AttrEditor
          table={table}
          initial={editing}
          submitLabel="Change"
          onCancel={() => setEditing(null)}
          onSubmit={(v) => {
            onChange({ table, action: "change", id: editing.id, ...v });
            setEditing(null);
          }}
        />
      )}
    </Box>
  );
}

function AddAttrForm({ onAdd }: { onAdd: (c: RadiusAttrChangeIn) => void }) {
  const [table, setTable] = useState<RadiusTable>("reply");
  const [open, setOpen] = useState(false);
  if (!open)
    return (
      <Box sx={{ mt: 1 }}>
        <Button size="small" onClick={() => setOpen(true)}>
          Add attribute
        </Button>
      </Box>
    );
  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
      <TextField select size="small" label="Kind" value={table} onChange={(e) => setTable(e.target.value as RadiusTable)} sx={{ maxWidth: 260 }}>
        <MenuItem value="reply">Reply item (sent to the router)</MenuItem>
        <MenuItem value="check">Check item (condition / limit)</MenuItem>
      </TextField>
      <AttrEditor
        key={table}
        table={table}
        initial={{ attribute: "", op: table === "reply" ? ":=" : ":=", value: "" }}
        submitLabel="Add"
        onCancel={() => setOpen(false)}
        onSubmit={(v) => {
          onAdd({ table, action: "add", ...v });
          setOpen(false);
        }}
      />
    </Box>
  );
}

function AttrEditor({
  table,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  table: RadiusTable;
  initial: RadiusAttr;
  submitLabel: string;
  onSubmit: (v: { attribute: string; op: string; value: string }) => void;
  onCancel: () => void;
}) {
  const [attribute, setAttribute] = useState(initial.attribute);
  const [op, setOp] = useState(RADIUS_OPS[table].includes(initial.op) ? initial.op : RADIUS_OPS[table][0]!);
  const [value, setValue] = useState(initial.value);
  const problem = radiusChangeProblem({ table, action: "add", attribute: attribute.trim(), op, value });
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap", mt: 1 }}>
      <TextField size="small" label="Attribute" value={attribute} onChange={(e) => setAttribute(e.target.value)} placeholder="e.g. WISPr-Bandwidth-Max-Down" />
      <TextField select size="small" label="Op" value={op} onChange={(e) => setOp(e.target.value)} sx={{ width: 90 }}>
        {RADIUS_OPS[table].map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </TextField>
      <TextField size="small" label="Value" value={value} onChange={(e) => setValue(e.target.value)} helperText={attribute || value ? problem || " " : " "} error={Boolean((attribute || value) && problem)} />
      <Button size="small" variant="outlined" disabled={Boolean(problem)} onClick={() => onSubmit({ attribute: attribute.trim(), op, value })}>
        {submitLabel}
      </Button>
      <Button size="small" onClick={onCancel}>
        Cancel
      </Button>
    </Box>
  );
}

/** After a save: a real RADIUS login with the subscriber's password, to see the reply. */
function CheckReply({ ns, accountId, username }: { ns: string; accountId: string; username: string }) {
  const identity = useIdentity();
  const [password, setPassword] = useState("");
  const test = useMutation({ mutationFn: (p: string) => testRadiusLogin(identity, ns, accountId, username, p) });
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2">Check the reply</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Type the subscriber's password to run a test login for {username} and see what RADIUS now sends back. The password is not kept.
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <PasswordField size="small" label="Subscriber password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        <Button
          variant="outlined"
          disabled={!password || test.isPending}
          onClick={() => {
            const p = password;
            setPassword("");
            test.mutate(p);
          }}
        >
          {test.isPending ? "Testing…" : "Test login"}
        </Button>
      </Box>
      {test.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {test.error instanceof Error ? test.error.message : String(test.error)}
        </Alert>
      )}
      {test.data && <RadiusTestView r={test.data} />}
    </Box>
  );
}

/** The change record. No hooks, so a test can render it. */
export function RadiusHistoryView({ changes }: { changes: RadiusAttrChangeRecord[] }) {
  if (changes.length === 0)
    return (
      <Typography variant="body2" color="text.secondary">
        No change recorded yet.
      </Typography>
    );
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Login</TableCell>
            <TableCell>Change</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((c, i) => (
            <TableRow key={`${c.changeSet}-${i}`}>
              <TableCell>{c.at}</TableCell>
              <TableCell>
                <code>{c.username}</code>
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>{radiusRecordLine(c)}</TableCell>
              <TableCell>{c.reason}</TableCell>
              <TableCell>
                {c.actorRole === "superadmin" ? "superadmin" : "Owner"} <Typography variant="caption">{c.actor}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
